// Inventário de rastreadores via /api/admin/trackers/*. Só verified_organizer
// (backend gateado pela policy `RequireVerifiedOrganizer`; o AuthGuard da rota
// é defesa em profundidade).

import { api } from './client';

export type TrackerStatus = 'active' | 'inactive';

export interface TrackerModel {
  id: string;
  manufacturer: string;
  model: string;
}

export interface Tracker {
  id: string;
  trackerModelId: string;
  manufacturer: string;
  model: string;
  imei: string;
  serialNumber: string;
  /// ICCID do chip; null = sem chip cadastrado.
  iccid: string | null;
  /// `yyyy-MM-dd` (data de calendário, sem fuso).
  purchaseDate: string;
  status: TrackerStatus;
  createdAt: string;
  updatedAt: string;
}

export interface TrackerPage {
  items: Tracker[];
  total: number;
  page: number;
  pageSize: number;
}

export interface TrackerUpsert {
  trackerModelId: string;
  imei: string;
  serialNumber: string;
  /// Vazio = sem chip (o backend grava null).
  iccid: string;
  purchaseDate: string;
}

/// Vínculo ativo rastreador ↔ inscrição (`TrackerAssignmentDto`). O sinal vem
/// da tabela bruta do listener, nas últimas 24 h; null = nada chegou.
export interface TrackerAssignment {
  id: string;
  trackerId: string;
  imei: string;
  serialNumber: string;
  registrationId: string;
  athleteName: string;
  bibNumber: number;
  /// `trk_<id da inscrição>` pra atleta sem conta.
  athleteUid: string | null;
  assignedAt: string;
  /// Última mensagem de qualquer tipo: o aparelho está ligado e conectado.
  lastMessageAt?: string | null;
  /// Último `+BUFF`: o aparelho ainda está despejando o buffer.
  lastBuffAt?: string | null;
  /// Última posição que entrou no live DESTE vínculo (só depois do
  /// `assignedAt`). É o que libera pra água: conectado sem GPS não aparece.
  lastFixAt?: string | null;
  /// `false` quando o servidor não conseguiu ler o sinal (a consulta é
  /// best-effort): os três campos acima vêm nulos por isso, não porque nada
  /// chegou. Ausente (servidor antigo) = lido. Review da #1365.
  signalAvailable?: boolean;
}

/// Check-in de rastreadores (#1172). Gate: organizador dono do evento, sem
/// precisar do inventário (que é só do `verified_organizer`).
export const trackerAssignmentsApi = {
  list: (eventId: string): Promise<TrackerAssignment[]> =>
    api<TrackerAssignment[]>(`/api/events/${encodeURIComponent(eventId)}/tracker-assignments`),

  /// Idempotente pro mesmo IMEI na mesma inscrição. Troca é explícita:
  /// `release` e depois `assign`.
  assign: (registrationId: string, imei: string): Promise<TrackerAssignment> =>
    api<TrackerAssignment>(`/api/registrations/${encodeURIComponent(registrationId)}/tracker`, {
      method: 'POST',
      body: { imei },
    }),

  release: (registrationId: string): Promise<void> =>
    api<void>(`/api/registrations/${encodeURIComponent(registrationId)}/tracker`, { method: 'DELETE' }),
};

export const trackersApi = {
  models: (): Promise<TrackerModel[]> => api<TrackerModel[]>('/api/admin/tracker-models'),

  /// `query` vem do `listQuery` (lib/trackers).
  list: (query: string): Promise<TrackerPage> => api<TrackerPage>(`/api/admin/trackers?${query}`),

  create: (body: TrackerUpsert): Promise<Tracker> =>
    api<Tracker>('/api/admin/trackers', { method: 'POST', body }),

  update: (id: string, body: TrackerUpsert): Promise<Tracker> =>
    api<Tracker>(`/api/admin/trackers/${encodeURIComponent(id)}`, { method: 'PUT', body }),

  /// Exclusão é lógica: inativar, nunca apagar.
  setStatus: (id: string, status: TrackerStatus): Promise<Tracker> =>
    api<Tracker>(`/api/admin/trackers/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      body: { status },
    }),
};
